import { useContext, useState } from 'react';

import { Formik } from 'formik';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { transformLoanPreferencesToRequest } from '@App/requests/transformToRequest';
import BackButton from '@Components/BackButton/BackButton';
import { HttpError } from '@Components/HttpError';
import ProgressMenu from '@Components/Progress/ProgressMenu';
import { loanPreferencesFormItems } from '@FormModels/eligibility/loanPreferences';
import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
} from '@Shared/helpers/input';
import { getOffersByProductCodes } from '@Shared/helpers/offer';
import { RateComponent } from '@Views/eligibility/loan-preferences/components/RateComponent';
import { QuestionPageForm } from '@Views/eligibility/loan-preferences/questions-page-view/QuestionPageForm';

import {
    getIsMoveToRates,
    getIsRetireBeforeRedeem,
    getYearItems,
} from './helpers';

const defaultValues = getDefaultValues(loanPreferencesFormItems);
const validationSchema = getValidationSchema(loanPreferencesFormItems);

export const QuestionsPageView = () => {
    const [isRatesVisible, setIsRatesVisible] = useState<boolean>(false);
    const [answers, setAnswers] = useState(null);
    const [error, setError] = useState<string | null>(null);

    const {
        loanDetails,
        saveQuestionnaireAnswers,
        applicationId,
        offers,
        applicants,
        incomings,
    } = useContext(ApplicationContext);

    const yearItems = getYearItems(loanDetails.term);
    const navigate = useNavigate();

    const onSubmit = async (values: any) => {
        const response = await saveQuestionnaireAnswers(
            applicationId,
            transformLoanPreferencesToRequest(values),
        );

        if (response.success) {
            const preferableOffers = response.preferableOffers
                ? getOffersByProductCodes(offers, response.preferableOffers)
                : [];

            const isRetireBeforeRedeem = getIsRetireBeforeRedeem(
                applicants[0].dateOfBirth,
                values.fullEarlyRepaymentYear,
                incomings[0].estimatedRetirementAge,
            );

            const isMoveToRates = getIsMoveToRates(values);

            if (isRetireBeforeRedeem || preferableOffers.length !== 2) {
                navigate('../book-call');
            }

            if (isMoveToRates) {
                setIsRatesVisible(true);
                setAnswers(values);
            }
        }

        if (!response.success) {
            setError(response.error);
        }

        mixpanel.track('Continue');
    };

    return (
        <div className='flex flex-row justify-between'>
            <div className='w-full sm:w-1/2 lg:w-2/5 mt-20 sm:mt-0'>
                <div className='hidden lg:block mb-8'>
                    <BackButton />
                </div>
                <div className='relative mb-15'>
                    <Formik
                        enableReinitialize
                        initialValues={defaultValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                    >
                        {(formikProps) => {
                            const { getError } = getFormHelpers(
                                loanPreferencesFormItems,
                                formikProps,
                            );

                            return (
                                <QuestionPageForm
                                    isRatesVisible={isRatesVisible}
                                    answers={answers}
                                    formikProps={formikProps}
                                    yearItems={yearItems}
                                    setIsRatesVisible={setIsRatesVisible}
                                    getError={getError}
                                />
                            );
                        }}
                    </Formik>

                    {isRatesVisible && <RateComponent />}
                </div>

                {error && <HttpError error={error} />}
            </div>

            <ProgressMenu />
        </div>
    );
};
